import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconPlus,
  IconRotate,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {
  useEncoderCatalog,
  useEncoderCodeSearch,
  useEncoderCptEdits,
} from "../../hooks/queries";
import type {
  CodeSearchCptMetadata,
  CodeSearchResult,
  CptEditsCombo,
  CptEditsResult,
} from "../../types/encoder";
import {
  cn,
  DEFAULT_CODE_YEARS,
  DEFAULT_POA_OPTIONS,
  encoderErrorMessage,
  isEncoderCptClaimType,
  isEncoderDxType,
  newScratchId,
  normalizeEncoderCode,
  todayDdMmYyyy,
} from "../../utils/encoder";
import { useEncoderUi } from "./context";
import {
  modifierOptionsForCode,
  type HcpcsModifierOption,
} from "./hcpcsModifiers";
import {
  CodeLabels,
  LabelChip,
  MergedCodeSearch,
  Spinner,
  useDebouncedValue,
} from "./shared";

type DxRow = {
  id: string;
  code: string;
  description: string;
  labels: string[];
  metadata?: CodeSearchResult["metadata"];
  poa: string;
  pdx: boolean;
  confirmed: boolean;
};

type LinkedDx = { code: string; description: string };

type PxRow = {
  id: string;
  code: string;
  description: string;
  type: string;
  labels: string[];
  metadata?: CodeSearchResult["metadata"];
  units: number;
  modifiers: string[];
  diagnosis: LinkedDx[];
  confirmed: boolean;
};

type AuditStatus = "idle" | "running" | "passed" | "failed";

type FacilityAlert = {
  column1: string;
  component: string;
  modifierIndicator: number;
  rationale: string;
};

type MueAlert = {
  code: string;
  description: string;
  units: number;
  limit: number;
};

type MenuPos = { top: number; left: number; width: number };

const CPT_EDITOR_TYPES = ["All", "Dx", "CPT", "PCS", "HCPCS"];
const DX_BROWSE_QUERY = "A";

const sameCode = (left: string, right: string): boolean =>
  Boolean(left) &&
  Boolean(right) &&
  normalizeEncoderCode(left) === normalizeEncoderCode(right);

const toDxRow = (result: CodeSearchResult): DxRow => ({
  id: newScratchId(),
  code: result.code,
  description: result.description,
  labels: result.labels ?? [],
  metadata: result.metadata,
  poa: "",
  pdx: false,
  confirmed: true,
});

const toPxRow = (result: CodeSearchResult): PxRow => ({
  id: newScratchId(),
  code: result.code,
  description: result.description,
  type: result.type,
  labels: result.labels ?? [],
  metadata: result.metadata,
  units: 1,
  modifiers: [],
  diagnosis: [],
  confirmed: true,
});

const metaValue = (
  cpt: CodeSearchCptMetadata | undefined,
  keys: string[]
): string => {
  if (!cpt) return "";
  const record = cpt as Record<string, unknown>;
  for (let i = 0; i < keys.length; i += 1) {
    const value = record[keys[i]];
    if (value === undefined || value === null || value === "") continue;
    return String(value);
  }
  return "";
};

const facilityAlerts = (rows: CptEditsResult[]): FacilityAlert[] => {
  const alerts: FacilityAlert[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const combos = (row.combo ?? []).filter(
      (combo: CptEditsCombo) => !combo.type || combo.type === "Facility"
    );
    for (let j = 0; j < combos.length; j += 1) {
      alerts.push({
        column1: row.column1,
        component: combos[j].code,
        modifierIndicator: combos[j].modifierIndicator,
        rationale: combos[j].rationale,
      });
    }
  }
  return alerts;
};

const useAnchoredMenu = (
  open: boolean,
  triggerRef: React.RefObject<HTMLElement>
): MenuPos => {
  const [pos, setPos] = useState<MenuPos>({ top: 0, left: 0, width: 280 });
  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.max(rect.width, 280);
      const left = Math.min(
        Math.max(8, rect.left),
        window.innerWidth - width - 8
      );
      const top = Math.min(rect.bottom + 4, window.innerHeight - 16);
      setPos({ top, left, width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, triggerRef]);
  return pos;
};

const ConfirmBox: React.FC<{
  confirmed: boolean;
  onToggle: () => void;
  label: string;
}> = ({ confirmed, onToggle, label }) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={confirmed}
    onClick={onToggle}
    className="inline-flex h-5 w-5 items-center justify-center rounded-[3px] border border-slate-300 bg-white"
  >
    {confirmed ? (
      <IconCheck size={14} stroke={3} className="text-green-600" />
    ) : null}
  </button>
);

const PickerTrigger: React.FC<{
  triggerRef: React.RefObject<HTMLButtonElement>;
  open: boolean;
  onToggle: () => void;
  display: string;
  empty?: boolean;
}> = ({ triggerRef, open, onToggle, display, empty }) => (
  <button
    ref={triggerRef}
    type="button"
    aria-expanded={open}
    onClick={onToggle}
    className="flex h-8 w-full min-w-[7.5rem] items-center justify-between gap-1 rounded-md border border-slate-200 bg-white px-2 text-left text-xs shadow-sm"
  >
    <span
      className={cn(
        "min-w-0 truncate",
        empty ? "text-slate-400" : "font-mono text-slate-800"
      )}
    >
      {display}
    </span>
    <IconChevronDown size={12} className="shrink-0 text-slate-400" />
  </button>
);

const SearchMenu: React.FC<{
  open: boolean;
  pos: MenuPos;
  menuRef: React.RefObject<HTMLDivElement>;
  query: string;
  onQuery: (value: string) => void;
  placeholder: string;
  children: React.ReactNode;
  width?: number;
}> = ({ open, pos, menuRef, query, onQuery, placeholder, children, width }) => {
  if (!open) return null;
  return createPortal(
    <div
      ref={menuRef}
      style={{
        top: pos.top,
        left: pos.left,
        width: width ?? Math.max(pos.width, 280),
      }}
      className="fixed z-[80] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <IconSearch size={14} className="shrink-0 text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={placeholder}
          className="h-7 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
      </div>
      <div className="max-h-64 overflow-auto py-1">{children}</div>
    </div>,
    document.body
  );
};

const CheckRow: React.FC<{
  checked: boolean;
  disabled?: boolean;
  highlight?: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ checked, disabled, highlight, onToggle, children }) => (
  <label
    data-selected={checked ? "true" : undefined}
    className={cn(
      "flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm",
      highlight || checked ? "bg-slate-100" : "hover:bg-slate-50",
      disabled && "cursor-not-allowed opacity-40"
    )}
  >
    <input
      type="checkbox"
      className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
    />
    <span className="min-w-0 leading-5">{children}</span>
  </label>
);

const ModifierPicker: React.FC<{
  value: string[];
  metadata?: CodeSearchResult["metadata"];
  onChange: (next: string[]) => void;
}> = ({ value, metadata, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pos = useAnchoredMenu(open, triggerRef);
  const options = useMemo(() => modifierOptionsForCode(metadata), [metadata]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (item) =>
        item.code.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const node = menuRef.current?.querySelector("[data-selected='true']");
    if (node && "scrollIntoView" in node) {
      (node as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [open, filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
      setQuery("");
    };
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [open]);

  const toggle = (item: HcpcsModifierOption) => {
    if (value.includes(item.code)) {
      onChange(value.filter((code) => code !== item.code));
      return;
    }
    if (value.length >= 5) return;
    onChange(value.concat([item.code]));
  };

  return (
    <div className="min-w-[7.5rem]">
      <PickerTrigger
        triggerRef={triggerRef}
        open={open}
        onToggle={() => setOpen((current) => !current)}
        display={value.length ? value.join(", ") : "—"}
        empty={!value.length}
      />
      <SearchMenu
        open={open}
        pos={pos}
        menuRef={menuRef}
        query={query}
        onQuery={setQuery}
        placeholder="Search..."
        width={Math.max(pos.width, 320)}
      >
        {filtered.length === 0 ? (
          <p className="px-3 py-2 text-sm text-slate-500">No modifiers found</p>
        ) : (
          filtered.map((item) => {
            const checked = value.includes(item.code);
            return (
              <CheckRow
                key={item.code}
                checked={checked}
                disabled={!checked && value.length >= 5}
                highlight={checked}
                onToggle={() => toggle(item)}
              >
                <span className="font-medium">{item.code}</span>
                {item.description ? ` - ${item.description}` : ""}
              </CheckRow>
            );
          })
        )}
      </SearchMenu>
    </div>
  );
};

const DxLinkPicker: React.FC<{
  year: number;
  value: LinkedDx[];
  claimDiagnoses: LinkedDx[];
  onChange: (next: LinkedDx[]) => void;
}> = ({ year, value, claimDiagnoses, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pos = useAnchoredMenu(open, triggerRef);
  const trimmed = query.trim();
  const searchTerm = trimmed || DX_BROWSE_QUERY;
  const debounced = useDebouncedValue(searchTerm, trimmed ? 300 : 0);
  const search = useEncoderCodeSearch(debounced, "Dx", year, {
    enabled: open,
    limit: 40,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
      setQuery("");
    };
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [open]);

  const options = useMemo(() => {
    const seen: Record<string, true> = {};
    const out: LinkedDx[] = [];
    const add = (code: string, description: string) => {
      const key = normalizeEncoderCode(code);
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push({ code, description });
    };
    for (let i = 0; i < value.length; i += 1) {
      add(value[i].code, value[i].description);
    }
    for (let i = 0; i < claimDiagnoses.length; i += 1) {
      add(claimDiagnoses[i].code, claimDiagnoses[i].description);
    }
    const results = search.data ?? [];
    for (let i = 0; i < results.length; i += 1) {
      add(results[i].code, results[i].description);
    }
    return out;
  }, [claimDiagnoses, search.data, value]);

  const toggle = (item: LinkedDx) => {
    if (value.some((row) => sameCode(row.code, item.code))) {
      onChange(value.filter((row) => !sameCode(row.code, item.code)));
      return;
    }
    if (value.length >= 4) return;
    onChange(value.concat([item]));
  };

  return (
    <div className="min-w-[9rem]">
      <PickerTrigger
        triggerRef={triggerRef}
        open={open}
        onToggle={() => setOpen((current) => !current)}
        display={
          value.length ? value.map((item) => item.code).join(", ") : "Select Dx"
        }
        empty={!value.length}
      />
      <SearchMenu
        open={open}
        pos={pos}
        menuRef={menuRef}
        query={query}
        onQuery={setQuery}
        placeholder="Search diagnoses..."
        width={Math.max(pos.width, 360)}
      >
        {search.isFetching && options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-slate-500">Searching…</p>
        ) : options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-slate-500">No diagnoses found</p>
        ) : (
          options.map((item, index) => {
            const checked = value.some((row) => sameCode(row.code, item.code));
            return (
              <CheckRow
                key={item.code}
                checked={checked}
                disabled={!checked && value.length >= 4}
                highlight={checked && index === 0}
                onToggle={() => toggle(item)}
              >
                <span className="font-medium">{item.code}</span>
                {item.description ? ` - ${item.description}` : ""}
              </CheckRow>
            );
          })
        )}
      </SearchMenu>
    </div>
  );
};

const CptMetaChips: React.FC<{ metadata?: CodeSearchResult["metadata"] }> = ({
  metadata,
}) => {
  const cpt = metadata?.CPT;
  const items = [
    { label: "SI", value: metaValue(cpt, ["SI"]) },
    { label: "APC", value: metaValue(cpt, ["APC"]) },
    { label: "Rank", value: metaValue(cpt, ["RANK", "Rank"]) },
    {
      label: "Global Days",
      value: metaValue(cpt, ["DAYS", "GLOBAL_DAYS"]),
    },
    { label: "Work RVU", value: metaValue(cpt, ["WORK_RVU"]) },
    { label: "Payment", value: "" },
  ];
  return (
    <div className="mt-1 inline-flex max-w-full flex-wrap items-center gap-x-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] text-sky-900">
      {items.map((item, index) => (
        <span key={item.label} className="inline-flex items-center gap-1">
          {index > 0 ? <span className="text-sky-400">|</span> : null}
          <span className="font-semibold">{item.label}:</span>
          <span>{item.value || "—"}</span>
        </span>
      ))}
    </div>
  );
};

const AuditBadge: React.FC<{
  active: boolean;
  status: AuditStatus;
  count: number;
}> = ({ active, status, count }) => {
  if (status === "running") {
    return (
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
    );
  }
  if (status === "passed") {
    return (
      <span
        className={cn(
          "inline-flex h-5 w-5 items-center justify-center rounded-full",
          active ? "bg-white text-emerald-500" : "bg-emerald-500 text-white"
        )}
      >
        <IconCheck size={12} stroke={3} />
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        className={cn(
          "inline-flex h-5 min-w-5 items-center justify-center gap-0.5 rounded-full px-1 text-[10px] font-bold",
          active ? "bg-white text-red-600" : "bg-red-500 text-white"
        )}
      >
        <IconAlertTriangle size={10} />
        {count}
      </span>
    );
  }
  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-200 px-1 text-[10px] font-semibold text-slate-600">
      {count}
    </span>
  );
};

const AuditTabButton: React.FC<{
  label: string;
  active: boolean;
  status: AuditStatus;
  count: number;
  onClick: () => void;
}> = ({ label, active, status, count, onClick }) => {
  const pill = active
    ? status === "passed"
      ? "border-emerald-500 bg-emerald-500 text-white"
      : status === "failed"
        ? "border-red-500 bg-red-500 text-white"
        : "border-slate-300 bg-slate-200 text-slate-800"
    : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-2 rounded-full border px-3 text-sm font-medium",
        pill
      )}
    >
      <span>{label}</span>
      <AuditBadge active={active} status={status} count={count} />
    </button>
  );
};

const auditPanelClass = (status: AuditStatus) =>
  status === "passed"
    ? "border-emerald-200 text-emerald-700"
    : status === "failed"
      ? "border-red-200 text-red-800"
      : "border-slate-200 text-slate-700";

const CptHcpcsEditorTab: React.FC = () => {
  const catalog = useEncoderCatalog();
  const { showToast } = useEncoderUi();
  const years = catalog.data?.codeYears?.length
    ? catalog.data.codeYears
    : DEFAULT_CODE_YEARS;
  const poaOptions = catalog.data?.poaOptions?.length
    ? catalog.data.poaOptions
    : DEFAULT_POA_OPTIONS;
  const [year, setYear] = useState(() =>
    years.includes(2026) ? 2026 : years[0] ?? 2026
  );
  const [type, setType] = useState("CPT");
  const [selected, setSelected] = useState<CodeSearchResult | null>(null);
  const [diagnoses, setDiagnoses] = useState<DxRow[]>([]);
  const [procedures, setProcedures] = useState<PxRow[]>([]);
  const [auditTab, setAuditTab] = useState<"ptp" | "mcd" | "mue">("ptp");
  const [ptpStatus, setPtpStatus] = useState<AuditStatus>("idle");
  const [ptpAlerts, setPtpAlerts] = useState<FacilityAlert[]>([]);
  const cptEdits = useEncoderCptEdits();
  const requestId = useRef(0);

  const confirmedCptCodes = useMemo(
    () =>
      procedures
        .filter((row) => row.confirmed && isEncoderCptClaimType(row.type))
        .map((row) => row.code),
    [procedures]
  );
  const ptpKey = confirmedCptCodes.join("|");
  const claimDiagnoses = useMemo(
    () =>
      diagnoses.map((row) => ({
        code: row.code,
        description: row.description,
      })),
    [diagnoses]
  );
  const mueAlerts = useMemo((): MueAlert[] => {
    const alerts: MueAlert[] = [];
    for (let i = 0; i < procedures.length; i += 1) {
      const row = procedures[i];
      if (!row.confirmed || !isEncoderCptClaimType(row.type)) continue;
      const limit = Number(metaValue(row.metadata?.CPT, ["MUE_UNIT", "MUE"]));
      if (!Number.isFinite(limit) || limit <= 0) continue;
      if (row.units > limit) {
        alerts.push({
          code: row.code,
          description: row.description,
          units: row.units,
          limit,
        });
      }
    }
    return alerts;
  }, [procedures]);
  const mueStatus: AuditStatus = !confirmedCptCodes.length
    ? "idle"
    : mueAlerts.length
      ? "failed"
      : "passed";

  useEffect(() => {
    if (!ptpKey) {
      setPtpStatus("idle");
      setPtpAlerts([]);
      return undefined;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    setPtpStatus("running");
    let cancelled = false;
    void cptEdits
      .mutateAsync({
        codes: ptpKey.split("|"),
        dateofService: todayDdMmYyyy(),
      })
      .then((payload) => {
        if (cancelled || requestId.current !== id) return;
        const alerts = facilityAlerts(payload.results ?? []);
        setPtpAlerts(alerts);
        setPtpStatus(alerts.length ? "failed" : "passed");
      })
      .catch((event) => {
        if (cancelled || requestId.current !== id) return;
        setPtpAlerts([]);
        setPtpStatus("failed");
        showToast(
          `PTP Edit(s) failed: ${encoderErrorMessage(event, "Unknown error")}`,
          "error"
        );
      });
    return () => {
      cancelled = true;
    };
    // Intentionally keyed only on confirmed CPT codes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptpKey]);

  const alreadyAdded = (code: string): boolean =>
    diagnoses.some((row) => sameCode(row.code, code)) ||
    procedures.some((row) => sameCode(row.code, code));

  const addSelected = () => {
    if (!selected) return;
    if (alreadyAdded(selected.code)) {
      showToast(`${selected.code} is already added`, "info");
      return;
    }
    if (isEncoderDxType(selected.type)) {
      setDiagnoses((current) => current.concat([toDxRow(selected)]));
    } else {
      setProcedures((current) => current.concat([toPxRow(selected)]));
    }
    setSelected(null);
  };

  const resetTool = () => {
    requestId.current += 1;
    setSelected(null);
    setDiagnoses([]);
    setProcedures([]);
    setType("CPT");
    setYear(years.includes(2026) ? 2026 : years[0] ?? 2026);
    setAuditTab("ptp");
    setPtpStatus("idle");
    setPtpAlerts([]);
  };

  const splitDxLabels = (labels: string[]) => ({
    chips: labels.filter((label) => label === "CC" || label === "MCC"),
    other: labels.filter((label) => label !== "CC" && label !== "MCC"),
  });

  const activeAuditStatus =
    auditTab === "ptp" ? ptpStatus : auditTab === "mue" ? mueStatus : "idle";

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="sticky top-0 z-20 border-b bg-white p-2">
        <div className="flex w-full items-center gap-2">
          <div className="w-[60%] min-w-0">
            <MergedCodeSearch
              year={year}
              years={years}
              type={type}
              types={CPT_EDITOR_TYPES}
              onYearChange={setYear}
              onTypeChange={setType}
              onSelect={setSelected}
              selected={selected}
            />
          </div>
          <button
            type="button"
            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-black px-3 text-sm font-medium text-white disabled:opacity-50"
            onClick={addSelected}
            disabled={!selected}
          >
            <IconPlus size={14} /> Add Code
          </button>
          <button
            type="button"
            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm text-white"
            onClick={resetTool}
          >
            <IconRotate size={14} /> Reset
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex w-[60%] min-h-0 flex-col overflow-hidden border-r">
          <div className="max-h-[38%] shrink-0 overflow-auto">
            <table className="w-full table-fixed text-left text-sm">
              <thead>
                <tr className="bg-[#f3e8ff] text-[11px] font-semibold uppercase tracking-wide text-fuchsia-950">
                  <th className="w-8 px-3 py-2">#</th>
                  <th className="w-20 px-2 py-2">ICD-10</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="w-16 px-2 py-2">POA</th>
                  <th className="w-12 px-2 py-2">PDx</th>
                  <th className="w-20 px-2 py-2">Confirm</th>
                  <th className="w-8 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {diagnoses.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="border-b border-slate-100 px-3 py-2.5 text-sm text-slate-400"
                    >
                      Add diagnosis codes to begin.
                    </td>
                  </tr>
                ) : (
                  diagnoses.map((row, index) => {
                    const labels = splitDxLabels(row.labels);
                    return (
                      <tr key={row.id} className="border-b border-slate-100 align-top">
                        <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                        <td className="px-2 py-2">
                          <div className="font-mono font-semibold">{row.code}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {labels.chips.map((label) => (
                              <LabelChip
                                key={label}
                                label={label}
                                colors={catalog.data?.labelColors}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <p>{row.description}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <CodeLabels
                              result={{
                                labels: labels.other,
                                metadata: row.metadata,
                              }}
                              colors={catalog.data?.labelColors}
                            />
                            {row.pdx ? (
                              <LabelChip
                                label="PDX"
                                colors={catalog.data?.labelColors}
                              />
                            ) : null}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={row.poa}
                            onChange={(event) =>
                              setDiagnoses((current) =>
                                current.map((item) =>
                                  item.id === row.id
                                    ? { ...item, poa: event.target.value }
                                    : item
                                )
                              )
                            }
                            className="h-8 rounded-md border border-slate-200 bg-white px-1 text-xs"
                          >
                            <option value="">—</option>
                            {poaOptions.map((option: { value: string }) => (
                              <option key={option.value} value={option.value}>
                                {option.value}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={row.pdx}
                            onChange={() =>
                              setDiagnoses((current) =>
                                current.map((item) => ({
                                  ...item,
                                  pdx: item.id === row.id ? !item.pdx : false,
                                }))
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <ConfirmBox
                            confirmed={row.confirmed}
                            label={`Confirm ${row.code}`}
                            onToggle={() =>
                              setDiagnoses((current) =>
                                current.map((item) =>
                                  item.id === row.id
                                    ? { ...item, confirmed: !item.confirmed }
                                    : item
                                )
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            aria-label={`Remove ${row.code}`}
                            onClick={() =>
                              setDiagnoses((current) =>
                                current.filter((item) => item.id !== row.id)
                              )
                            }
                          >
                            <IconX size={14} className="text-slate-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#ddd6fe] text-[11px] font-semibold uppercase tracking-wide text-violet-950">
                  <th className="w-8 px-3 py-2">#</th>
                  <th className="w-16 px-2 py-2">CPT</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="w-[4.5rem] px-2 py-2">Units</th>
                  <th className="w-28 px-2 py-2">Modifier</th>
                  <th className="w-40 px-2 py-2">Diagnosis</th>
                  <th className="w-20 px-2 py-2">Confirm</th>
                  <th className="w-8 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {procedures.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-3 py-2.5 text-sm text-slate-400"
                    >
                      Add procedure codes to begin.
                    </td>
                  </tr>
                ) : (
                  procedures.map((row, index) => (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 align-top"
                    >
                      <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                      <td className="px-2 py-2 font-mono font-semibold">
                        {row.code}
                      </td>
                      <td className="px-2 py-2">
                        <p className="line-clamp-2">{row.description}</p>
                        <CptMetaChips metadata={row.metadata} />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          min={1}
                          value={row.units}
                          onChange={(event) => {
                            const next = Number.parseInt(
                              event.target.value,
                              10
                            );
                            setProcedures((current) =>
                              current.map((item) =>
                                item.id === row.id
                                  ? {
                                      ...item,
                                      units: Number.isFinite(next)
                                        ? Math.max(1, next)
                                        : 1,
                                    }
                                  : item
                              )
                            );
                          }}
                          className="h-8 w-12 rounded-md border-0 bg-yellow-100 text-center text-sm"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <ModifierPicker
                          value={row.modifiers}
                          metadata={row.metadata}
                          onChange={(modifiers) =>
                            setProcedures((current) =>
                              current.map((item) =>
                                item.id === row.id
                                  ? { ...item, modifiers }
                                  : item
                              )
                            )
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <DxLinkPicker
                          year={year}
                          value={row.diagnosis}
                          claimDiagnoses={claimDiagnoses}
                          onChange={(diagnosis) =>
                            setProcedures((current) =>
                              current.map((item) =>
                                item.id === row.id
                                  ? { ...item, diagnosis }
                                  : item
                              )
                            )
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <ConfirmBox
                          confirmed={row.confirmed}
                          label={`Confirm ${row.code}`}
                          onToggle={() =>
                            setProcedures((current) =>
                              current.map((item) =>
                                item.id === row.id
                                  ? { ...item, confirmed: !item.confirmed }
                                  : item
                              )
                            )
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          aria-label={`Remove ${row.code}`}
                          onClick={() =>
                            setProcedures((current) =>
                              current.filter((item) => item.id !== row.id)
                            )
                          }
                        >
                          <IconX size={14} className="text-slate-400" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex w-[40%] min-h-0 flex-col bg-white p-3">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <AuditTabButton
                label="PTP Edit(s)"
                active={auditTab === "ptp"}
                status={ptpStatus}
                count={ptpAlerts.length}
                onClick={() => setAuditTab("ptp")}
              />
              <AuditTabButton
                label="MCD Check(s)"
                active={auditTab === "mcd"}
                status="idle"
                count={0}
                onClick={() => setAuditTab("mcd")}
              />
              <AuditTabButton
                label="MUE Check(s)"
                active={auditTab === "mue"}
                status={mueStatus}
                count={mueAlerts.length}
                onClick={() => setAuditTab("mue")}
              />
            </div>
            <div
              className={cn(
                "mt-3 min-h-0 flex-1 overflow-auto rounded-lg border px-3 py-2 text-sm",
                auditPanelClass(activeAuditStatus)
              )}
            >
              {auditTab === "ptp" ? (
                ptpStatus === "idle" ? (
                  <p>Add CPT codes to run PTP Edit(s).</p>
                ) : ptpStatus === "running" ? (
                  <Spinner label="Running PTP Edit(s)..." />
                ) : ptpStatus === "passed" ? (
                  <p className="font-medium text-emerald-700">
                    No PTP Edit(s) Alerts
                  </p>
                ) : (
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-red-800">
                        <th className="px-2 py-1">Column 1</th>
                        <th className="px-2 py-1">Component</th>
                        <th className="px-2 py-1">Modifier</th>
                        <th className="px-2 py-1">Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ptpAlerts.map((alert, index) => (
                        <tr
                          key={`${alert.column1}-${alert.component}-${index}`}
                          className="border-b border-red-100"
                        >
                          <td className="px-2 py-1.5 font-mono">
                            {alert.column1}
                          </td>
                          <td className="px-2 py-1.5 font-mono">
                            {alert.component}
                          </td>
                          <td className="px-2 py-1.5">
                            <span
                              title={
                                alert.modifierIndicator === 0
                                  ? "Modifier not allowed"
                                  : "Modifier allowed"
                              }
                              className={cn(
                                "inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold",
                                alert.modifierIndicator === 0
                                  ? "bg-red-200 text-red-900"
                                  : "bg-green-200 text-green-900"
                              )}
                            >
                              {alert.modifierIndicator === 0
                                ? "0 not allowed"
                                : `${alert.modifierIndicator} allowed`}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">{alert.rationale}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : auditTab === "mcd" ? (
                <p>MCD Check(s): not run</p>
              ) : mueStatus === "idle" ? (
                <p>Add CPT codes to run MUE Check(s).</p>
              ) : mueStatus === "passed" ? (
                <p className="font-medium text-emerald-700">
                  No MUE Check(s) Alerts
                </p>
              ) : (
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase text-red-800">
                      <th className="px-2 py-1">Code</th>
                      <th className="px-2 py-1">Units</th>
                      <th className="px-2 py-1">MUE Limit</th>
                      <th className="px-2 py-1">Alert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mueAlerts.map((alert) => (
                      <tr key={alert.code} className="border-b border-red-100">
                        <td className="px-2 py-1.5 font-mono">{alert.code}</td>
                        <td className="px-2 py-1.5">{alert.units}</td>
                        <td className="px-2 py-1.5">{alert.limit}</td>
                        <td className="px-2 py-1.5 text-red-700">
                          Units exceed the facility MUE limit
                          {alert.description ? ` for ${alert.description}` : ""}
                          .
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CptHcpcsEditorTab;
